import axios from 'axios'
import Vue from 'vue'

const axiosWithoutCredentials = axios.create({
	withCredentials: false
})

type SubscribePayload = {
	scopes: string[]
	eid?: string
	url?: string
}

type Outlier = {
	_id: string
	visionId: string
}

const accountLoadPromises = new Map<string, Promise<any>>()

export default {
	name: 'creatorVision',
	namespaced: true,
	state: {
		profiles: {},
		mediaOutliers: {},
		mediaOutliersTotal: 0,
		mediaErrors: {},
		loading: new Set(),
		mediaSnapshots: {},
		cachedThumbnails: {},
		hasManuallyLoadedOutliers: false
	},
	mutations: {
		SET_HAS_MANUALLY_LOADED_OUTLIERS(state, value) {
			state.hasManuallyLoadedOutliers = value
		},
		ON_DEAUTHENTICATE(state) {
			state.hasManuallyLoadedOutliers = false
			state.mediaOutliers = {}
			state.mediaOutliersTotal = 0
			state.profiles = {}
			state.mediaSnapshots = {}
		},
		SET_PROFILES(state, profiles) {
			profiles.forEach(profile => {
				Vue.set(state.profiles, profile.eid, profile)
			})
		},
		SET_PROFILE(state, profile) {
			Vue.set(state.profiles, profile.eid, profile)
		},
		SET_MEDIA_OUTLIERS(state, mediaOutliers) {
			mediaOutliers.forEach(mediaOutlier => {
				Vue.set(state.mediaOutliers, mediaOutlier.visionId, {
					...(state.mediaOutliers[mediaOutlier.visionId] ?? {}),
					...mediaOutlier
				})
			})
		},
		SET_MEDIA_OUTLIERS_TOTAL(state, total) {
			state.mediaOutliersTotal = total
		},
		SET_MEDIA_ERRORS(state, mediaErrors) {
			Vue.set(state, 'mediaErrors', mediaErrors)
		},
		SET_MEDIA_OUTLIER_SNAPSHOTS(state, payload) {
			if (!payload || !payload.mediaId) {
				return
			}
			Vue.set(state.mediaSnapshots, payload.mediaId, payload.data)
		},
		SET_LOADING_START(state, keyword) {
			state.loading.add(keyword)
		},
		SET_LOADING_END(state, keyword) {
			state.loading.delete(keyword)
		},
		REGISTER_MEDIA_THUMBNAIL(state, src: string) {
			if (!src) {
				return
			}
			Vue.set(state.cachedThumbnails, src, true)
		}
	},
	actions: {
		async loadProfiles(context) {
			context.commit('SET_LOADING_START', 'profiles')
			const creatorVisionServer = context.rootState.app.creatorVisionServer
			try {
				const response = await axiosWithoutCredentials.get(
					context.rootState.app.creatorVisionServer + '/profiles'
				)
				context.commit('SET_PROFILES', response.data)
				context.commit('SET_LOADING_END', 'profiles')
			} catch (e) {
				console.error(e)
			}
		},
		async loadMediaOutliers(context, factor) {
			if (!factor) {
				return []
			}
			context.commit('SET_HAS_MANUALLY_LOADED_OUTLIERS', true)
			context.commit('SET_LOADING_START', factor)
			try {
				let allData = []
				let nextCursor = null
				let hasMore = true

				while (hasMore) {
					const params: any = { factor }
					if (nextCursor) {
						params.cursorId = nextCursor.id
					}

					const response = await axios.get('/api/media/outliers', { params })

					if (response.data.pagination.totalItems > -1) {
						context.commit(
							'SET_MEDIA_OUTLIERS_TOTAL',
							response.data.pagination.totalItems
						)
					}

					allData = [...allData, ...response.data.data]
					context.commit('SET_MEDIA_OUTLIERS', allData)

					nextCursor = response.data.pagination.nextCursor
					hasMore = !!nextCursor
				}
			} catch (e) {
				console.error(e)
			}
			context.commit('SET_LOADING_END', factor)
		},
		async loadMediaOutliersForAccount(context, account) {
			if (accountLoadPromises.has(account)) {
				try {
					return await accountLoadPromises.get(account)
				} catch (e) {
					console.error(e)
				}
			}
			context.commit('SET_LOADING_START', account)
			try {
				const promise = axios.get('/api/media/outliers?account=' + account)
				accountLoadPromises.set(account, promise)
				const response = await promise
				context.commit('SET_MEDIA_OUTLIERS', response.data.data)
			} catch (e) {
				console.error(e)
			}
			accountLoadPromises.delete(account)
			context.commit('SET_LOADING_END', account)
		},
		async loadMediaSnapshots(context, mediaId) {
			if (context.state.mediaSnapshots[mediaId]) {
				return context.state.mediaSnapshots[mediaId]
			}
			context.commit('SET_LOADING_START', mediaId)
			try {
				const response = await axios.get('/api/media/' + mediaId)
				context.commit('SET_MEDIA_OUTLIER_SNAPSHOTS', {
					mediaId,
					data: response.data
				})
				return response.data
			} catch (e) {
				console.error(e)
			} finally {
				context.commit('SET_LOADING_END', mediaId)
			}
		},
		async loadReport(context, reportRequestPayload) {
			try {
				const response = await axiosWithoutCredentials.post(
					context.rootState.app.creatorVisionServer + '/report',
					reportRequestPayload
				)
				return response.data
			} catch (e) {
				console.error(e)
			}
		},
		async loadMediaErrors(context) {
			try {
				const response = await axios.get('/api/media/errors')
				context.commit('SET_MEDIA_ERRORS', response.data)
				return response.data
			} catch (e) {
				console.error(e)
			}
		},
		async subscribe(context, data: SubscribePayload) {
			const creatorVisionServer = context.rootState.app.creatorVisionServer
			try {
				const url = new URL(creatorVisionServer + '/subscribe')
				// add get params
				url.searchParams.set('subscriber', 'cv')

				if (!data.scopes || data.scopes.length === 0) {
					data.scopes = ['statistics']
				}
				url.searchParams.set('scopes', data.scopes.join(','))
				url.searchParams.set('forceUpdate', 'true')
				if (data.url) {
					url.searchParams.set('url', data.url)
				}
				if (data.eid) {
					url.searchParams.set('eid', data.eid)
				}
				const response = await axiosWithoutCredentials.get(url.toString())
				context.commit('SET_PROFILE', response.data.data)
			} catch (e) {
				console.error(e)
			}
		}
	},
	getters: {
		profiles: state => Object.values(state.profiles) || [],
		mediaOutliers: (state, getters, rootState, rootGetters) =>
			Object.values(state.mediaOutliers) || [],
		mediaSnapshotById: state => (mediaId: string) => state.mediaSnapshots[mediaId],
		mediaOutliersForAccount: (state, getters) => account =>
			getters.mediaOutliers.filter(mo => mo.socialEntity.account === account),
		mediaOutliersForSelectedAccount: (state, getters, rootState, rootGetters) =>
			getters.mediaOutliersForAccount(rootGetters['account/selected']._id),
		isThumbnailCached: state => (src: string) => Boolean(src && state.cachedThumbnails[src])
	}
}
